// §V cards selection — Detection Engine System 1 (spec §3.3–3.6).
//
// Formalized from `sweep_sectionF.py`. Каждая выжившая конфигурация → карточка:
//   filled cell (Section C) → positive; иначе F-family stub (§3.4); ничего на
//   странице → F.7 scatter. Coverage: min 1 / max 3 (§3.6); стабы внизу.
//
// `stubFires` — точный mirror прототипа (для parity-теста gating-логики),
// независимый от coverage/main-pattern. `selectCards` — продакшн-выход.

import { series, gapAt } from './input.js';
import { layerIndex } from './layers.js';
import { THRESHOLDS as T } from './thresholds.js';
import { FILLED } from './gating.js';

// current-singleton cell id by layer (s382): L1..L7 → A.2.1.1 … A.2.7.1. Написанные
// ячейки существуют по слою; движок теперь их привязывает, CD не мэтчит по слою.
const currentSingletonCell = (L) => `A.2.${layerIndex(L) + 1}.1`;
import {
  isParallelWhole, isDivergence, isLateConvergence, isAntiCorrelation,
  currentSingleton, historicalSingleton, isDrifting, isLastToCollapse,
  isFirstToCollapse, isVShape, isCycling, troughIndex, detectFieldLevel,
} from './detectors.js';
import { interiorExtrema } from './features.js';

const pairKey = (a, b) => `${a}+${b}`;

// ── F.3 / F.5 sub-variant routing (engine fixes, §3.4) ──────────────────────

/** F.3 mode-routing: collapse-режимы → temporal; drift/historical → spatial. */
function f3Route(modes) {
  return modes.some((m) => m === 'last-to-collapse' || m === 'first-to-collapse')
    ? 'temporal'
    : 'spatial';
}

/** F.5 layer-gating: A/W/L → active-rebuild; V/G → biological. */
function f5Variant(L) {
  if (L === 'Vitality' || L === 'Growth') return 'biological';
  return 'active-rebuild'; // Authorship / Wanting / Lasting
}

/**
 * Точный mirror прототипа: какие F-стабы зажглись бы по ВСЕЙ детектированной
 * геометрии (без coverage-потолка, без main-pattern). field-level (stage-
 * aligned, engine fix) суппрессит F.5. Используется для parity-теста.
 */
export function stubFires(stages, active) {
  const field = detectFieldLevel(stages, active);
  const out = { F1: [], F2: [], F3: [], F5: [], F6: [], F7: false };
  let anyGeometry = Boolean(field);

  for (let i = 0; i < active.length; i++)
    for (let j = i + 1; j < active.length; j++) {
      const a = active[i];
      const b = active[j];
      if (isParallelWhole(stages, a, b)) {
        anyGeometry = true;
        if (!FILLED.parallelWhole(a, b)) out.F1.push(pairKey(a, b));
      }
      if (isDivergence(stages, a, b)) {
        anyGeometry = true;
        out.F2.push(pairKey(a, b)); // все divergence cells пусты
      }
      if (isLateConvergence(stages, a, b)) anyGeometry = true;
      if (isAntiCorrelation(stages, a, b)) anyGeometry = true;
    }

  for (const L of active) {
    const v = series(stages, L);
    const cs = currentSingleton(stages, active, L);
    const hs = historicalSingleton(stages, active, L);
    const dr = isDrifting(stages, active, L);
    const ltc = isLastToCollapse(stages, active, L);
    const ftc = isFirstToCollapse(stages, active, L);
    const vs = isVShape(v);
    const cyc = isCycling(v);
    if (cs || hs.length || dr || ltc || ftc || vs || cyc) anyGeometry = true;

    if (vs && !field) {
      const guard = Math.abs(gapAt(stages, active, L, stages.length - 1));
      if (guard <= T.VSHAPE_GUARD && !FILLED.vShape(L))
        out.F5.push({ layer: L, guard, variant: f5Variant(L) });
    }

    const modes = [];
    if (hs.length && !FILLED.historical(L)) modes.push('historical');
    if (dr) modes.push('drifting');
    if (ltc && !FILLED.lastToCollapse(L)) modes.push('last-to-collapse');
    if (ftc) modes.push('first-to-collapse');
    if (modes.length) out.F3.push({ layer: L, modes, route: f3Route(modes) });

    if (cyc && !FILLED.cycling(L)) out.F6.push(L);
  }

  if (!anyGeometry) out.F7 = true;
  return out;
}

// ── evidence stages (§3.5) ──────────────────────────────────────────────────

export function evidenceStages(d, stages, active) {
  const N = stages.length;
  switch (d.type) {
    case 'current-singleton':
      return [N - 1];
    case 'historical-singleton':
      return d.stages ? d.stages.slice() : [];
    case 'drifting-singleton':
      return [0, N - 1];
    case 'last-to-collapse': {
      const held = [];
      for (let i = 0; i < N - 1; i++)
        if (gapAt(stages, active, d.layers[0], i) >= T.SINGLETON_GAP) held.push(i);
      return [...held, N - 1];
    }
    case 'first-to-collapse': {
      const early = [];
      for (let i = 0; i < N - 1; i++)
        if (gapAt(stages, active, d.layers[0], i) <= -T.SINGLETON_GAP) early.push(i);
      return [...early, N - 1];
    }
    case 'v-shape-recovery':
      return [d.trough ?? troughIndex(series(stages, d.layers[0])), N - 1];
    case 'singleton-cycling': {
      const { extremaMax, extremaMin } = interiorExtrema(series(stages, d.layers[0]));
      return [...extremaMax, ...extremaMin].sort((a, b) => a - b);
    }
    case 'field-level-recovery':
      return [d.trough, N - 1];
    case 'divergence-over-time':
    case 'group-vs-group':
      return [0, reversalStage(d, stages, 'diverge')];
    case 'late-convergence':
      return [0, reversalStage(d, stages, 'converge')];
    case 'parallel-whole':
    case 'triplet-as-one':
      return [minSpreadStage(d.layers, stages)];
    default:
      return [];
  }
}

/** Этап, где gap пары впервые достиг/пересёк порог расхождения/сближения. */
function reversalStage(d, stages, mode) {
  const [a, b] = d.layers;
  if (!a || !b || !stages[0].values) return stages.length - 1;
  for (let i = 0; i < stages.length; i++) {
    const gap = Math.abs(stages[i].values[a] - stages[i].values[b]);
    if (mode === 'diverge' && gap >= T.DIVERGENCE_MIN_GAP) return i;
    if (mode === 'converge' && i > 0 && gap <= T.PARALLEL_MAX_GAP) return i;
  }
  return stages.length - 1;
}

/** Этап максимального совпадения группы (min spread). */
function minSpreadStage(layers, stages) {
  let best = 0;
  let bestSpread = Infinity;
  for (let i = 0; i < stages.length; i++) {
    const vals = layers.map((L) => stages[i].values[L]);
    const spread = Math.max(...vals) - Math.min(...vals);
    if (spread < bestSpread) {
      bestSpread = spread;
      best = i;
    }
  }
  return best;
}

// ── per-detection card classification ───────────────────────────────────────

/**
 * Detection → card descriptor (positive / stub / null).
 * @returns { detection, kind:'positive'|'stub', stub, cell, variant, route, evidence } | null
 */
export function classifyCard(d, stages, active) {
  const L = d.layers[0];
  const ev = () => evidenceStages(d, stages, active);
  const positive = (cell) => ({ detection: d, kind: 'positive', stub: null, cell: cell ?? null, evidence: ev() });
  const stub = (s, extra = {}) => ({ detection: d, kind: 'stub', stub: s, cell: null, evidence: ev(), ...extra });

  switch (d.type) {
    case 'current-singleton':
      // wanting CS-below → routing A.2.5.1.#11/#9; иначе ячейка по слою A.2.X.1 (s382)
      return positive(d.routing ?? currentSingletonCell(L));
    case 'historical-singleton':
      return FILLED.historical(L)
        ? positive('A.2.4.2')
        : stub('F.3', { route: 'spatial', modes: ['historical'] });
    case 'drifting-singleton':
      return stub('F.3', { route: 'spatial', modes: ['drifting'] });
    case 'last-to-collapse':
      return FILLED.lastToCollapse(L)
        ? positive('A.2.5.4')
        : stub('F.3', { route: 'temporal', modes: ['last-to-collapse'] });
    case 'first-to-collapse':
      return stub('F.3', { route: 'temporal', modes: ['first-to-collapse'] });
    case 'v-shape-recovery':
      if (FILLED.vShape(L)) return positive(L === 'Security' ? 'A.2.2.6' : 'A.2.6.6');
      return d.guard <= T.VSHAPE_GUARD
        ? stub('F.5', { variant: f5Variant(L) })
        : null; // guard>1.5 уже снят в mutex, но на всякий
    case 'singleton-cycling':
      return FILLED.cycling(L) ? positive('A.5.1.7') : stub('F.6');
    case 'parallel-whole':
      return FILLED.parallelWhole(d.layers[0], d.layers[1])
        ? positive('A.1')
        : stub('F.1');
    case 'divergence-over-time':
      return stub('F.2'); // divergence всегда дыра
    case 'late-convergence':
      return FILLED.lateConvergence(d.layers[0], d.layers[1]) ? positive('A.1.lc') : null;
    case 'anti-correlation':
      return null; // нет карточки в v1
    case 'triplet-as-one':
      return positive('A.3.1');
    case 'group-vs-group':
      return positive('A.3.2');
    case 'field-level-recovery':
      return positive('A.4.2');
    case 'scatter':
      return stub('F.7');
    default:
      return null; // parallel-partial / untiered → нет карточки
  }
}

// ── production card selection with coverage (§3.6) ──────────────────────────

/**
 * Финальный набор §V карточек. main первой; реальные карточки ≤ 3; стабы внизу;
 * минимум 1 (пусто → F.7 scatter).
 */
export function selectCards(ranked, main, stages, active) {
  const cards = ranked
    .map((d) => classifyCard(d, stages, active))
    .filter(Boolean);

  // dedupe по (kind, type, layers).
  const seen = new Set();
  const unique = [];
  for (const c of cards) {
    const key = `${c.kind}:${c.detection.type}:${c.detection.layers.join('|')}`;
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(c);
    }
  }

  const real = unique.filter((c) => c.kind === 'positive');
  const stubs = unique.filter((c) => c.kind === 'stub' && c.stub !== 'F.7');

  const realKept = real.slice(0, T.CARDS_MAX);

  let stubsKept;
  if (realKept.length === 0) {
    // all-uncovered → 1–2 сильные заглушки (§3.6).
    stubsKept = stubs.slice(0, 2);
    if (stubsKept.length === 0) {
      // ничего на странице → F.7 scatter (min coverage 1).
      const f7 = unique.find((c) => c.stub === 'F.7') || classifyCard(main, stages, active);
      return { cards: [f7].filter(Boolean), real: [], stubs: [f7].filter(Boolean) };
    }
  } else {
    stubsKept = stubs.slice(0, Math.max(0, T.CARDS_MAX - realKept.length));
  }

  return { cards: [...realKept, ...stubsKept], real: realKept, stubs: stubsKept };
}
